import React, { useEffect, useState } from "react";
import "./CallReviewTable.css";
import { FaEdit } from "react-icons/fa";
import CallReviewSheetPopup from "../Popups/CallReviewSheetPopup";
import { postCallReview } from "../../../operations/services/postCallReview";
import { notify } from "../../../common/Helper/notification";
import { findAgentEmail } from "../../Helper/findAgentEmail";
import { useDispatch, useSelector } from "react-redux";
import { setMNScore } from "../../../redux/slices/operationHomeSlice";
import { deriveCallReviewSections, getTotal } from "./callReviewScoring";

/* ─── Section Component ─── */

const Section = ({ title, fields, values, maxValues, weightageFactor }) => {
  const totalMax = getTotal(maxValues);
  const totalAchieved = values.reduce(
    (sum, score, i) => sum + (score ?? 0) * (weightageFactor[i] ?? 1),
    0,
  );

  return (
    <div className="sub-section">
      <h4 className="sub-title">{title}</h4>

      <div className="table-wrapper">
        <table className="call-review-table section-5col">
          <thead>
            <tr>
              <th>Scoring Category</th>
              <th>Score</th>
              <th>Weightage Factor</th>
              <th>Max Score</th>
              <th>Achieved Score</th>
            </tr>
          </thead>

          <tbody>
            {fields.map((item, index) => {
              const achieved =
                (values[index] ?? 0) * (weightageFactor[index] ?? 1);
              return (
                <tr key={index}>
                  <td>{item}</td>
                  <td className="points">{values[index] ?? 0}</td>
                  <td className="points">
                    {(weightageFactor[index] ?? 1).toFixed(2)}
                  </td>
                  <td className="points">
                    {(maxValues[index] ?? 0).toFixed(2)}
                  </td>
                  <td className="points">{achieved.toFixed(2)}</td>
                </tr>
              );
            })}

            <tr className="total-row">
              <td>Total</td>
              <td></td>
              <td></td>
              <td>{totalMax.toFixed(2)}</td>
              <td>{totalAchieved.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── MAIN COMPONENT ─── */

const CallReviewTable = ({
  callReviewData,
  callId,
  onRefetch,
  agentName,
  memberName,
  reviewDate,
  vonageCallId,
  magicHealthId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();

  const loginData = useSelector((state) => state.login.login.data);
  const userRole = loginData?.data[0]?.Role;

  // Reviewer name for the call-review payload — combines whichever of
  // FirstName/LastName is present so a partial profile still yields a name
  // instead of an empty string.
  const reviewerUser = loginData?.data?.[0];
  const reviewerName = [reviewerUser?.FirstName, reviewerUser?.LastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  // Computed safely with optional chaining so the useEffect below can run
  // unconditionally (hooks must not be called after an early return).
  const mnScore = parseFloat(
    (callReviewData?.average?.outOf5 ?? 0).toFixed(2)
  );

  useEffect(() => {
    if (!callId || !callReviewData) return;
    dispatch(setMNScore({ callId, score: mnScore }));
  }, [callId, callReviewData, dispatch, mnScore]);

  if (!callReviewData) return null;

  const { emailRecipients = [] } = callReviewData;
  const derived = deriveCallReviewSections(callReviewData);
  const { utSection, sSection, oSection, totals, average, totalScore5 } =
    derived.summary;
  const usingTools = derived.usingTools;
  const sales = derived.sales;
  const objections = derived.objections;

  const agentEmail = findAgentEmail(agentName, emailRecipients)?.email || "";

  console.log("[CallReviewTable] agentName:", agentName);
  console.log("[CallReviewTable] emailRecipients:", emailRecipients);
  console.log("[CallReviewTable] matched agentEmail:", agentEmail);

  const handleEditClick = () => setIsOpen(true);

  const mapYesNo = (value) => (value === 1 ? "Yes" : "No");

  // `options.sendEmail` reuses this same encrypted POST for the "Email to
  // Agent" action — the backend responds with emailSent/emailError on that
  // same call so we don't need a second endpoint.
  const handleSaveCallReview = async (data, options = {}) => {
    try {
      if (!callId) return notify.info("Call ID is missing");
      if (!data) return notify.info("No data to save");

      setSaving(true);

      const payload = {
        callid: callId,
        UT1: mapYesNo(data.usingTools?.[0]),
        UT2: {
          1: mapYesNo(data.usingTools?.[1]),
          2: mapYesNo(data.usingTools?.[2]),
          3: mapYesNo(data.usingTools?.[3]),
          4: mapYesNo(data.usingTools?.[4]),
        },
        UT3: mapYesNo(data.usingTools?.[5]),
        UT4: mapYesNo((data.usingTools?.[6] ?? 0) >= 1 ? 1 : 0),
        S1: mapYesNo(data.sales?.[0]),
        S2: mapYesNo(data.sales?.[1]),
        S3: mapYesNo(data.sales?.[2]),
        O5: mapYesNo(data.objections?.[0]),
        O1: mapYesNo(data.objections?.[1]),
        O2: mapYesNo(data.objections?.[2]),
        O3: mapYesNo(data.objections?.[3]),
        O4: mapYesNo(data.objections?.[4]),
        WhatWentWell: data.whatWentWell || "",
        WhatCanImprove: data.whatCanImprove || "",
        GeneralComments: data.generalComments || "",
      };

      // Always send an explicit boolean — true when the user clicked
      // "Email to Agent", false for a plain Save/Not Now — rather than
      // omitting the key, so the backend can rely on it being present.
      payload.sendEmail = !!options.sendEmail;
      if (options.sendEmail && options.agentEmail) {
        payload.agentEmail = options.agentEmail;
      }

      // Reviewer identity + the page the review was submitted from, for the
      // backend's audit trail / email template. Sourced from Redux and the
      // live browser URL — no extra API calls needed.
      payload.reviewerName = reviewerName;
      payload.convoUrl = typeof window !== "undefined" ? window.location.href : "";

      console.log("[CallReviewTable] postCallReview payload:", payload);

      const response = await postCallReview(payload);
      console.log("[CallReviewTable] postCallReview response:", response);

      if (options.sendEmail) {
        if (response?.emailSent) {
          notify.success("Feedback emailed to the agent successfully");
        } else {
          notify.error(response?.emailError || "Failed to email feedback to the agent");
        }
      } else {
        notify.success("Call review saved successfully");
      }

      setIsOpen(false);
      onRefetch?.();
      return response;
    } catch (error) {
      notify.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="call-review-container">
      <div className="header-row">
         <div className="edit-icon-wrapper" onClick={() => setIsOpen(true)}>
  <FaEdit className="edit-icon" />
  <span className="edit-text">Edit Scores</span>
</div>
      </div>

      {/* SUMMARY */}
      <div className="table-wrapper">
        <table className="call-review-table summary-5col">
          <thead>
            <tr>
              <th>Scoring Category</th>
              <th>Max Score</th>
              <th>Achieved Score</th>
              {/* <th>Final</th> */}
              <th>Score out of 5</th>
              <th>% Score</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>Using the Tools</td>
              <td>{utSection.max ?? 0}</td>
              <td>{(utSection.achieved ?? 0).toFixed(2)}</td>
              {/* <td>{(utSection.final ?? 0).toFixed(3)}</td> */}
              <td>{(utSection.outOf5 ?? 0).toFixed(2)}</td>
              <td>{(utSection.percentage ?? 0).toFixed(1)}%</td>
            </tr>

            <tr>
              <td>Sales</td>
              <td>{sSection.max ?? 0}</td>
              <td>{(sSection.achieved ?? 0).toFixed(2)}</td>
              {/* <td>{(sSection.final ?? 0).toFixed(3)}</td> */}
              <td>{(sSection.outOf5 ?? 0).toFixed(2)}</td>
              <td>{(sSection.percentage ?? 0).toFixed(1)}%</td>
            </tr>

            <tr>
              <td>Objections</td>
              <td>{oSection.max ?? 0}</td>
              <td>{(oSection.achieved ?? 0).toFixed(2)}</td>
              {/* <td>{(oSection.final ?? 0).toFixed(3)}</td> */}
              <td>{(oSection.outOf5 ?? 0).toFixed(2)}</td>
              <td>{(oSection.percentage ?? 0).toFixed(1)}%</td>
            </tr>

            <tr className="total-row">
              <td>Total</td>
              <td>{totals.max ?? 0}</td>
              <td>{(totals.achieved ?? 0).toFixed(2)}</td>
              {/* <td></td> */}
              <td>{totalScore5.toFixed(2)}</td>
              <td></td>
            </tr>

            <tr className="average-row">
              <td>Average Score</td>
              <td></td>
              <td></td>
              {/* <td>{(average.final ?? 0).toFixed(2)}</td> */}
              <td>{(average.outOf5 ?? 0).toFixed(2)}</td>
              <td>{(average.percentage ?? 0).toFixed(2)}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* DETAILS */}
      <Section
        title={usingTools.title}
        fields={usingTools.fields}
        values={usingTools.values}
        maxValues={usingTools.maxValues}
        weightageFactor={usingTools.weightageFactor}
      />

      <Section
        title={sales.title}
        fields={sales.fields}
        values={sales.values}
        maxValues={sales.maxValues}
        weightageFactor={sales.weightageFactor}
      />

      <Section
        title={objections.title}
        fields={objections.fields}
        values={objections.values}
        maxValues={objections.maxValues}
        weightageFactor={objections.weightageFactor}
      />

      {/* POPUP */}
      <CallReviewSheetPopup
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSaveCallReview}
        isSaving={saving}
        initialData={{
          usingTools: usingTools.values,
          sales: sales.values,
          objections: objections.values,
          whatWentWell: callReviewData?.feedback?.whatWentWell || "",
          whatCanImprove: callReviewData?.feedback?.whatCanImprove || "",
          generalComments: callReviewData?.feedback?.generalComments || "",
        }}
        reviewData={callReviewData}
        callId={callId}
        agentName={agentName}
        agentEmail={agentEmail}
        memberName={memberName}
        reviewDate={reviewDate}
        vonageCallId={vonageCallId}
        magicHealthId={magicHealthId}
      />
    </div>
  );
};

export default CallReviewTable;
